import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import groupService from '../../shared/services/groupService';
import { getErrorMessage } from '../../shared/utils/errorUtils';
import type {
  Group,
  GroupMembership,
  GroupStatistics,
  CreateGroupDTO,
  UpdateGroupDTO,
  GroupMember,
  GroupTestResult,
  JoinRequest,
} from '../../shared/types/group';

// State interface
interface GroupState {
  // Manager state
  myGroups: Group[];
  currentGroup: Group | null;
  groupMembers: GroupMember[];
  groupStatistics: GroupStatistics | null;
  groupTestResults: GroupTestResult[];
  pendingJoinRequests: JoinRequest[];  // Join requests waiting for approval

  // Student state
  myMemberships: GroupMembership[];
  previewGroup: Group | null;

  // UI state
  loading: boolean;
  error: string | null;
}

// Initial state
const initialState: GroupState = {
  myGroups: [],
  currentGroup: null,
  groupMembers: [],
  groupStatistics: null,
  groupTestResults: [],
  pendingJoinRequests: [],
  myMemberships: [],
  previewGroup: null,
  loading: false,
  error: null,
};

// ==================== Async Thunks ====================

// Manager thunks

export const createGroup = createAsyncThunk(
  'group/createGroup',
  async (data: CreateGroupDTO, { rejectWithValue }) => {
    try {
      const group = await groupService.createGroup(data);
      return group;
    } catch (error: unknown) {
      return rejectWithValue(getErrorMessage(error, 'Failed to create group'));
    }
  }
);

export const fetchMyGroups = createAsyncThunk(
  'group/fetchMyGroups',
  async (_, { rejectWithValue }) => {
    try {
      const groups = await groupService.getMyGroups();
      return groups;
    } catch (error: unknown) {
      return rejectWithValue(getErrorMessage(error, 'Failed to fetch groups'));
    }
  }
);

export const fetchGroupById = createAsyncThunk(
  'group/fetchGroupById',
  async (id: string, { rejectWithValue }) => {
    try {
      const group = await groupService.getGroupById(id);
      return group;
    } catch (error: unknown) {
      return rejectWithValue(getErrorMessage(error, 'Failed to fetch group'));
    }
  }
);

export const updateGroup = createAsyncThunk(
  'group/updateGroup',
  async ({ id, data }: { id: string; data: UpdateGroupDTO }, { rejectWithValue }) => {
    try {
      const group = await groupService.updateGroup(id, data);
      return group;
    } catch (error: unknown) {
      return rejectWithValue(getErrorMessage(error, 'Failed to update group'));
    }
  }
);

export const deleteGroup = createAsyncThunk(
  'group/deleteGroup',
  async (id: string, { rejectWithValue }) => {
    try {
      await groupService.deleteGroup(id);
      return id;
    } catch (error: unknown) {
      return rejectWithValue(getErrorMessage(error, 'Failed to delete group'));
    }
  }
);

export const regenerateInviteCode = createAsyncThunk(
  'group/regenerateInviteCode',
  async (id: string, { rejectWithValue }) => {
    try {
      const group = await groupService.regenerateInviteCode(id);
      return group;
    } catch (error: unknown) {
      return rejectWithValue(getErrorMessage(error, 'Failed to regenerate invite code'));
    }
  }
);

export const fetchGroupMembers = createAsyncThunk(
  'group/fetchGroupMembers',
  async (id: string, { rejectWithValue }) => {
    try {
      const members = await groupService.getGroupMembers(id);
      return members;
    } catch (error: unknown) {
      return rejectWithValue(getErrorMessage(error, 'Failed to fetch members'));
    }
  }
);

export const addMember = createAsyncThunk(
  'group/addMember',
  async ({ groupId, userId }: { groupId: string; userId: string }, { rejectWithValue }) => {
    try {
      await groupService.addMember(groupId, userId);
      // Refetch members after adding
      const members = await groupService.getGroupMembers(groupId);
      return members;
    } catch (error: unknown) {
      return rejectWithValue(getErrorMessage(error, 'Failed to add member'));
    }
  }
);

export const removeMember = createAsyncThunk(
  'group/removeMember',
  async ({ groupId, userId }: { groupId: string; userId: string }, { rejectWithValue }) => {
    try {
      await groupService.removeMember(groupId, userId);
      // Refetch members after removing
      const members = await groupService.getGroupMembers(groupId);
      return members;
    } catch (error: unknown) {
      return rejectWithValue(getErrorMessage(error, 'Failed to remove member'));
    }
  }
);

export const fetchGroupStatistics = createAsyncThunk(
  'group/fetchGroupStatistics',
  async (id: string, { rejectWithValue }) => {
    try {
      const statistics = await groupService.getGroupStatistics(id);
      return statistics;
    } catch (error: unknown) {
      return rejectWithValue(getErrorMessage(error, 'Failed to fetch statistics'));
    }
  }
);

export const fetchGroupTestResults = createAsyncThunk(
  'group/fetchGroupTestResults',
  async (id: string, { rejectWithValue }) => {
    try {
      const results = await groupService.getGroupTestResults(id);
      return results;
    } catch (error: unknown) {
      return rejectWithValue(getErrorMessage(error, 'Failed to fetch test results'));
    }
  }
);

// Student thunks

export const previewGroupByCode = createAsyncThunk(
  'group/previewGroupByCode',
  async (inviteCode: string, { rejectWithValue }) => {
    try {
      const group = await groupService.previewGroup(inviteCode);
      return group;
    } catch (error: unknown) {
      return rejectWithValue(getErrorMessage(error, 'Invalid invite code'));
    }
  }
);

export const joinGroup = createAsyncThunk(
  'group/joinGroup',
  async (inviteCode: string, { rejectWithValue }) => {
    try {
      const membership = await groupService.joinGroup(inviteCode);
      return membership;
    } catch (error: unknown) {
      return rejectWithValue(getErrorMessage(error, 'Failed to join group'));
    }
  }
);

export const fetchMyMemberships = createAsyncThunk(
  'group/fetchMyMemberships',
  async (_, { rejectWithValue }) => {
    try {
      const memberships = await groupService.getMyMemberships();
      return memberships;
    } catch (error: unknown) {
      return rejectWithValue(getErrorMessage(error, 'Failed to fetch memberships'));
    }
  }
);

export const leaveGroup = createAsyncThunk(
  'group/leaveGroup',
  async (groupId: string, { rejectWithValue }) => {
    try {
      await groupService.leaveGroup(groupId);
      return groupId;
    } catch (error: unknown) {
      return rejectWithValue(getErrorMessage(error, 'Failed to leave group'));
    }
  }
);

// Manager approval thunks

export const fetchPendingJoinRequests = createAsyncThunk(
  'group/fetchPendingJoinRequests',
  async (groupId: string, { rejectWithValue }) => {
    try {
      const requests = await groupService.getPendingJoinRequests(groupId);
      return requests;
    } catch (error: unknown) {
      return rejectWithValue(getErrorMessage(error, 'Failed to fetch join requests'));
    }
  }
);

export const approveJoinRequest = createAsyncThunk(
  'group/approveJoinRequest',
  async ({ groupId, membershipId }: { groupId: string; membershipId: string }, { rejectWithValue }) => {
    try {
      const membership = await groupService.approveJoinRequest(groupId, membershipId);
      return { groupId, membership };
    } catch (error: unknown) {
      return rejectWithValue(getErrorMessage(error, 'Failed to approve join request'));
    }
  }
);

export const rejectJoinRequest = createAsyncThunk(
  'group/rejectJoinRequest',
  async ({ groupId, membershipId }: { groupId: string; membershipId: string }, { rejectWithValue }) => {
    try {
      await groupService.rejectJoinRequest(groupId, membershipId);
      return { groupId, membershipId };
    } catch (error: unknown) {
      return rejectWithValue(getErrorMessage(error, 'Failed to reject join request'));
    }
  }
);

// ==================== Slice ====================

const groupSlice = createSlice({
  name: 'group',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearPreviewGroup: (state) => {
      state.previewGroup = null;
    },
    clearCurrentGroup: (state) => {
      state.currentGroup = null;
      state.groupMembers = [];
      state.groupStatistics = null;
      state.groupTestResults = [];
    },
  },
  extraReducers: (builder) => {
    // Create Group
    builder
      .addCase(createGroup.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createGroup.fulfilled, (state, action: PayloadAction<Group>) => {
        state.loading = false;
        state.myGroups.unshift(action.payload);
        state.currentGroup = action.payload;
      })
      .addCase(createGroup.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Fetch My Groups
    builder
      .addCase(fetchMyGroups.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMyGroups.fulfilled, (state, action: PayloadAction<Group[]>) => {
        state.loading = false;
        state.myGroups = action.payload;
      })
      .addCase(fetchMyGroups.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Fetch Group By ID
    builder
      .addCase(fetchGroupById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchGroupById.fulfilled, (state, action: PayloadAction<Group>) => {
        state.loading = false;
        state.currentGroup = action.payload;
      })
      .addCase(fetchGroupById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Update Group
    builder
      .addCase(updateGroup.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateGroup.fulfilled, (state, action: PayloadAction<Group>) => {
        state.loading = false;
        state.currentGroup = action.payload;
        // Update in myGroups list
        const index = state.myGroups.findIndex((g) => g.id === action.payload.id);
        if (index !== -1) {
          state.myGroups[index] = action.payload;
        }
      })
      .addCase(updateGroup.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Delete Group
    builder
      .addCase(deleteGroup.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteGroup.fulfilled, (state, action: PayloadAction<string>) => {
        state.loading = false;
        state.myGroups = state.myGroups.filter((g) => g.id !== action.payload);
        if (state.currentGroup?.id === action.payload) {
          state.currentGroup = null;
        }
      })
      .addCase(deleteGroup.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Regenerate Invite Code
    builder
      .addCase(regenerateInviteCode.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(regenerateInviteCode.fulfilled, (state, action: PayloadAction<Group>) => {
        state.loading = false;
        state.currentGroup = action.payload;
        // Update in myGroups list
        const index = state.myGroups.findIndex((g) => g.id === action.payload.id);
        if (index !== -1) {
          state.myGroups[index] = action.payload;
        }
      })
      .addCase(regenerateInviteCode.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Fetch Group Members
    builder
      .addCase(fetchGroupMembers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchGroupMembers.fulfilled, (state, action: PayloadAction<GroupMember[]>) => {
        state.loading = false;
        state.groupMembers = action.payload;
      })
      .addCase(fetchGroupMembers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Add Member
    builder
      .addCase(addMember.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addMember.fulfilled, (state, action: PayloadAction<GroupMember[]>) => {
        state.loading = false;
        state.groupMembers = action.payload;
      })
      .addCase(addMember.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Remove Member
    builder
      .addCase(removeMember.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(removeMember.fulfilled, (state, action: PayloadAction<GroupMember[]>) => {
        state.loading = false;
        state.groupMembers = action.payload;
      })
      .addCase(removeMember.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Fetch Group Statistics
    builder
      .addCase(fetchGroupStatistics.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchGroupStatistics.fulfilled, (state, action: PayloadAction<GroupStatistics>) => {
        state.loading = false;
        state.groupStatistics = action.payload;
      })
      .addCase(fetchGroupStatistics.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Fetch Group Test Results
    builder
      .addCase(fetchGroupTestResults.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchGroupTestResults.fulfilled, (state, action: PayloadAction<GroupTestResult[]>) => {
        state.loading = false;
        state.groupTestResults = action.payload;
      })
      .addCase(fetchGroupTestResults.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Preview Group By Code
    builder
      .addCase(previewGroupByCode.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(previewGroupByCode.fulfilled, (state, action: PayloadAction<Group>) => {
        state.loading = false;
        state.previewGroup = action.payload;
      })
      .addCase(previewGroupByCode.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Join Group
    builder
      .addCase(joinGroup.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(joinGroup.fulfilled, (state, action: PayloadAction<GroupMembership>) => {
        state.loading = false;
        state.myMemberships.unshift(action.payload);
        state.previewGroup = null;
      })
      .addCase(joinGroup.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Fetch My Memberships
    builder
      .addCase(fetchMyMemberships.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMyMemberships.fulfilled, (state, action: PayloadAction<GroupMembership[]>) => {
        state.loading = false;
        state.myMemberships = action.payload;
      })
      .addCase(fetchMyMemberships.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Leave Group
    builder
      .addCase(leaveGroup.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(leaveGroup.fulfilled, (state, action: PayloadAction<string>) => {
        state.loading = false;
        state.myMemberships = state.myMemberships.filter((m) => m.group_id !== action.payload);
      })
      .addCase(leaveGroup.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Fetch Pending Join Requests
    builder
      .addCase(fetchPendingJoinRequests.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPendingJoinRequests.fulfilled, (state, action: PayloadAction<JoinRequest[]>) => {
        state.loading = false;
        state.pendingJoinRequests = action.payload;
      })
      .addCase(fetchPendingJoinRequests.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Approve Join Request
    builder
      .addCase(approveJoinRequest.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(approveJoinRequest.fulfilled, (state, action) => {
        state.loading = false;
        // Remove from pending requests
        state.pendingJoinRequests = state.pendingJoinRequests.filter(
          (r) => r.membership_id !== action.payload.membership.id
        );
        // Increment member count in currentGroup
        if (state.currentGroup) {
          state.currentGroup.member_count += 1;
        }
      })
      .addCase(approveJoinRequest.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Reject Join Request
    builder
      .addCase(rejectJoinRequest.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(rejectJoinRequest.fulfilled, (state, action) => {
        state.loading = false;
        // Remove from pending requests
        state.pendingJoinRequests = state.pendingJoinRequests.filter(
          (r) => r.membership_id !== action.payload.membershipId
        );
      })
      .addCase(rejectJoinRequest.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, clearPreviewGroup, clearCurrentGroup } = groupSlice.actions;
export default groupSlice.reducer;
